import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Logo from "../components/Logo";

export default function WidgetDocs() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const installCode = `<script src="https://botsaathi.com/widget.js"></script>
<script>
  BotSaathi.init({
    apiKey: "YOUR_API_KEY"
  });
</script>`;

  const advancedCode = `BotSaathi.init({
  apiKey: "YOUR_API_KEY",
  title: "Chat with us",
  welcomeMessage: "Hi! How can we help?",
  placeholder: "Type your message...",
  primaryColor: "#25d366"
});`;

  const copyCode = () => {
    navigator.clipboard.writeText(installCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={s.wrapper}>
      <Helmet>
        <title>Free AI Chat Widget for Website — BotSaathi | Add Live Chat in 2 Minutes</title>
        <meta name="description" content="Add a free AI-powered chat widget to your website in 2 minutes. Auto-replies with AI, captures leads, works on any website — WordPress, Shopify, HTML, React. No coding needed." />
        <meta name="keywords" content="free chat widget for website, AI chat widget, website chatbot free, live chat widget, add chat to website, free chatbot for website, AI website assistant, lead capture widget, chat bubble website, free live chat, website chat plugin, chatbot for WordPress, chatbot for Shopify" />
        <meta property="og:title" content="Free AI Chat Widget for Your Website — BotSaathi" />
        <meta property="og:description" content="Add AI-powered live chat to any website in 2 minutes. Free forever. Captures leads automatically." />
        <link rel="canonical" href="https://botsaathi.com/widget" />
      </Helmet>

      {/* Header */}
      <header style={s.header}>
        <div onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <Logo size={28} showText={true} />
        </div>
        <button onClick={() => navigate("/signup")} style={s.headerBtn}>Get API Key →</button>
      </header>

      <main style={s.main}>

        {/* Hero */}
        <div style={s.hero}>
          <div style={s.badge}>✨ FREE FOREVER</div>
          <h1 style={s.h1}>AI Chat Widget for<br />Your Website</h1>
          <p style={s.sub}>
            Add an AI-powered chat bubble to any website in 2 minutes.
            Auto-replies with AI. Captures leads. Completely free.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => navigate("/signup")} style={s.primaryBtn}>Get Started Free</button>
            <a href="#install" style={s.secondaryBtn}>View Installation →</a>
          </div>
        </div>

        {/* Features */}
        <div style={s.featureRow}>
          {[
            ["🤖", "AI-Powered Replies", "Claude AI answers visitor questions naturally — in English, Hindi, or any language"],
            ["🎯", "Lead Capture", "Automatically saves visitor name, phone and interest to your dashboard"],
            ["⚡", "2-Minute Setup", "Copy one script tag. Paste in your website. Done."],
            ["🌍", "Works Everywhere", "WordPress, Shopify, Wix, HTML, React, Next.js — any website"],
          ].map(([icon, title, desc], i) => (
            <div key={i} style={s.featureCard}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>{icon}</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{title}</div>
              <div style={{ fontSize: 13, color: "#666", lineHeight: 1.7 }}>{desc}</div>
            </div>
          ))}
        </div>

        {/* Installation */}
        <div id="install" style={s.section}>
          <h2 style={s.h2}>📦 Installation</h2>
          <p style={s.p}>Add this code before the closing <code style={s.inlineCode}>&lt;/body&gt;</code> tag of your website:</p>

          <div style={s.codeBlock}>
            <button onClick={copyCode} style={s.copyBtn}>{copied ? "✓ Copied!" : "Copy"}</button>
            <pre style={s.pre}>{installCode}</pre>
          </div>

          <div style={s.steps}>
            <div style={s.step}>
              <div style={s.stepNum}>1</div>
              <div>
                <div style={s.stepTitle}>Sign up free</div>
                <div style={s.stepDesc}>Create your account at botsaathi.com/signup — takes 30 seconds</div>
              </div>
            </div>
            <div style={s.step}>
              <div style={s.stepNum}>2</div>
              <div>
                <div style={s.stepTitle}>Get your API key</div>
                <div style={s.stepDesc}>Find it in your dashboard under Settings</div>
              </div>
            </div>
            <div style={s.step}>
              <div style={s.stepNum}>3</div>
              <div>
                <div style={s.stepTitle}>Paste the code</div>
                <div style={s.stepDesc}>Add the script to your website. Chat bubble appears instantly.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Customization */}
        <div style={s.section}>
          <h2 style={s.h2}>🎨 Customization</h2>
          <p style={s.p}>Customize the widget with these options:</p>
          <div style={s.codeBlock}>
            <pre style={s.pre}>{advancedCode}</pre>
          </div>

          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Option</th>
                <th style={s.th}>Description</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["apiKey", "Your unique API key (required)"],
                ["title", "Chat header title"],
                ["welcomeMessage", "First message visitors see"],
                ["placeholder", "Input placeholder text"],
                ["primaryColor", "Widget color (hex code)"],
              ].map(([opt, desc], i) => (
                <tr key={i}>
                  <td style={s.td}><code style={s.inlineCode}>{opt}</code></td>
                  <td style={s.td}>{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Platform guides */}
        <div style={s.section}>
          <h2 style={s.h2}>🔌 Platform Guides</h2>
          <div style={s.platformGrid}>
            {[
              ["WordPress", "Appearance → Theme Editor → footer.php → paste before </body>. Or use 'Insert Headers and Footers' plugin."],
              ["Shopify", "Online Store → Themes → Edit Code → theme.liquid → paste before </body>"],
              ["Wix", "Settings → Custom Code → Add Code to Body - End"],
              ["HTML Website", "Open your HTML file → paste before </body> tag → save & upload"],
              ["React / Next.js", "Add the script in your index.html or use next/script with strategy='lazyOnload'"],
              ["Webflow", "Project Settings → Custom Code → Footer Code → paste & publish"],
            ].map(([platform, guide], i) => (
              <div key={i} style={s.platformCard}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: "#25d366" }}>{platform}</div>
                <div style={{ fontSize: 13, color: "#666", lineHeight: 1.7 }}>{guide}</div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div style={s.section}>
          <h2 style={s.h2}>❓ Frequently Asked Questions</h2>
          {[
            ["Is the chat widget really free?", "Yes! The widget with AI replies and lead capture is free forever. It shows a small 'Powered by BotSaathi' badge. Paid plans remove the badge and add WhatsApp integration, analytics, and more."],
            ["Do I need coding knowledge?", "No. Just copy-paste one script tag. If you can edit your website, you can install this."],
            ["Which AI powers the replies?", "Claude AI by Anthropic — one of the most advanced AI models. It understands context and replies naturally in any language."],
            ["Where do captured leads go?", "All leads appear in your BotSaathi dashboard with name, phone, interest, and full conversation history."],
            ["Does it work on mobile?", "Yes — fully responsive. On mobile it opens as a full-screen chat."],
            ["Can I use it with WhatsApp?", "Yes! BotSaathi also offers WhatsApp automation. Web chat + WhatsApp leads all in one dashboard."],
          ].map(([q, a], i) => (
            <div key={i} style={s.faqItem}>
              <div style={s.faqQ}>{q}</div>
              <div style={s.faqA}>{a}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={s.cta}>
          <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 12, fontFamily: "Georgia, serif" }}>
            Add AI Chat to Your Website Today
          </h2>
          <p style={{ color: "#666", marginBottom: 24 }}>Free forever. 2-minute setup. No credit card required.</p>
          <button onClick={() => navigate("/signup")} style={s.primaryBtn}>🚀 Get Started Free</button>
        </div>

      </main>

      <footer style={s.footer}>
        <span style={{ color: "#444", fontSize: 13 }}>© 2026 BotSaathi · <a href="/" style={{ color: "#25d366", textDecoration: "none" }}>Home</a> · <a href="/privacy-policy" style={{ color: "#555", textDecoration: "none" }}>Privacy</a></span>
      </footer>
    </div>
  );
}

const s = {
  wrapper: { background: "#060608", minHeight: "100vh", color: "#f0f0f0", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 40px", borderBottom: "1px solid rgba(255,255,255,0.05)", position: "sticky", top: 0, background: "rgba(6,6,8,0.9)", backdropFilter: "blur(20px)", zIndex: 100 },
  headerBtn: { background: "#25d366", color: "#000", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  main: { maxWidth: 900, margin: "0 auto", padding: "0 24px" },
  hero: { textAlign: "center", padding: "80px 0 60px" },
  badge: { display: "inline-block", background: "rgba(37,211,102,0.1)", border: "1px solid rgba(37,211,102,0.25)", borderRadius: 20, padding: "5px 16px", fontSize: 12, color: "#25d366", fontWeight: 700, marginBottom: 24, letterSpacing: "0.05em" },
  h1: { fontSize: 44, fontWeight: 700, lineHeight: 1.15, marginBottom: 20, fontFamily: "Georgia, serif" },
  sub: { fontSize: 17, color: "#888", lineHeight: 1.8, maxWidth: 540, margin: "0 auto 32px" },
  primaryBtn: { background: "#25d366", color: "#000", padding: "13px 28px", borderRadius: 8, fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer", boxShadow: "0 0 24px rgba(37,211,102,0.2)" },
  secondaryBtn: { background: "transparent", border: "1px solid rgba(255,255,255,0.1)", color: "#f0f0f0", padding: "13px 28px", borderRadius: 8, fontSize: 15, textDecoration: "none", display: "inline-block" },
  featureRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 60 },
  featureCard: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: 24 },
  section: { marginBottom: 60 },
  h2: { fontSize: 26, fontWeight: 700, marginBottom: 16, fontFamily: "Georgia, serif" },
  p: { fontSize: 15, color: "#888", lineHeight: 1.8, marginBottom: 16 },
  inlineCode: { background: "rgba(255,255,255,0.08)", padding: "2px 8px", borderRadius: 4, fontSize: 13, fontFamily: "monospace", color: "#25d366" },
  codeBlock: { position: "relative", background: "#0d0d10", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 20, marginBottom: 24, overflow: "auto" },
  pre: { margin: 0, fontSize: 13, fontFamily: "monospace", color: "#a5e8bc", lineHeight: 1.7, whiteSpace: "pre-wrap" },
  copyBtn: { position: "absolute", top: 12, right: 12, background: "rgba(37,211,102,0.15)", border: "1px solid rgba(37,211,102,0.3)", borderRadius: 6, color: "#25d366", padding: "5px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600 },
  steps: { display: "flex", flexDirection: "column", gap: 16 },
  step: { display: "flex", gap: 16, alignItems: "flex-start" },
  stepNum: { width: 32, height: 32, borderRadius: "50%", background: "rgba(37,211,102,0.12)", border: "1px solid rgba(37,211,102,0.3)", color: "#25d366", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0 },
  stepTitle: { fontSize: 15, fontWeight: 700, marginBottom: 4 },
  stepDesc: { fontSize: 13, color: "#777", lineHeight: 1.6 },
  table: { width: "100%", borderCollapse: "collapse", marginTop: 8 },
  th: { textAlign: "left", padding: "10px 14px", fontSize: 12, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid rgba(255,255,255,0.08)" },
  td: { padding: "12px 14px", fontSize: 14, color: "#aaa", borderBottom: "1px solid rgba(255,255,255,0.04)" },
  platformGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 },
  platformCard: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 20 },
  faqItem: { borderBottom: "1px solid rgba(255,255,255,0.05)", padding: "18px 0" },
  faqQ: { fontSize: 15, fontWeight: 700, marginBottom: 8 },
  faqA: { fontSize: 14, color: "#888", lineHeight: 1.8 },
  cta: { textAlign: "center", padding: "60px 20px", background: "rgba(37,211,102,0.03)", border: "1px solid rgba(37,211,102,0.1)", borderRadius: 20, marginBottom: 60 },
  footer: { textAlign: "center", padding: "32px 20px", borderTop: "1px solid rgba(255,255,255,0.05)" },
};